// src/components/steps/OtpCodeInput.tsx
import React, { useRef } from 'react';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { inputCss } from '@/shared/borisFields';

const Boxes = styled.div`
  display: flex;
  gap: ${Spacing.x1}px;
  justify-content: center;
`;

// Reuses the shared Boris input token (`inputCss` — the same source every
// RgTextField uses: 60px tall, 15px radius, 1.5px line border, ink-border +
// soft ink glow on focus), then overrides only what a single-digit box needs:
// fixed 44px width (6 boxes + gaps clear a 320px viewport), no side padding,
// centered mono digit (Boris renders code entry — `.rg-input.mbi` — in mono).
const Box = styled.input`
  ${inputCss()}
  width: 44px;
  padding: 0;
  text-align: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 24px;
  &:focus {
    border-color: ${BorisScreener.ink};
    box-shadow: 0 0 0 3px rgba(28, 31, 33, 0.07);
  }
  &:disabled {
    background: ${BorisScreener.sand};
    color: ${BorisScreener.inkFaint};
  }
`;

interface OtpCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  disabled?: boolean;
  label?: string;
}

export const OtpCodeInput: React.FC<OtpCodeInputProps> = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  label = 'Digit',
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const focusBox = (index: number) => {
    const clamped = Math.max(0, Math.min(index, length - 1));
    refs.current[clamped]?.focus();
  };

  const handleChange = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');

    // Paste (or SMS autofill) of multiple digits: fill from this box onward.
    if (digits.length > 1) {
      const merged = (value.slice(0, index) + digits).slice(0, length);
      onChange(merged);
      focusBox(index + digits.length);
      return;
    }

    const chars = Array.from({ length }, (_, i) => value[i] ?? '');
    chars[index] = digits; // '' clears, single digit sets
    onChange(chars.join(''));
    if (digits && index < length - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      focusBox(index - 1);
    }
  };

  return (
    <Boxes role="group" aria-label={label}>
      {Array.from({ length }).map((_, i) => (
        <Box
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`${label} ${i + 1}`}
          value={value[i] ?? ''}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
        />
      ))}
    </Boxes>
  );
};
